import { Link } from "react-router-dom";
import { Badge, Button, Card } from "@vb/ui";
import { Seo } from "../../lib/seo.js";
import { Section } from "../../components/Section.js";

interface Row {
  ggr: string;
  revShare: string;
  licensing: string;
  delta: string;
}

const COMPARISON: Row[] = [
  {
    ggr: "$25k / mo",
    revShare: "$2,500",
    licensing: "$5,800",
    delta: "Rev-share wins by $3,300",
  },
  {
    ggr: "$75k / mo",
    revShare: "$7,500",
    licensing: "$10,000",
    delta: "Rev-share wins by $2,500",
  },
  {
    ggr: "$150k / mo",
    revShare: "$15,000",
    licensing: "$15,250",
    delta: "Roughly break-even",
  },
  {
    ggr: "$400k / mo",
    revShare: "$40,000",
    licensing: "$24,000",
    delta: "Licensing wins by $16,000",
  },
];

export function RevShareVsLicensing() {
  return (
    <>
      <Seo
        title="Revenue share or fixed licensing — what's better for a mid-tier crypto operator? · Vault Breaker Studios"
        description="Where Spribe, Smartsoft, and Hacksaw deal sheets actually land in 2026, why integration fees dwarf list pricing, and the GGR threshold above which fixed licensing wins."
        path="/blog/rev-share-vs-licensing"
      />

      <Section style={{ paddingBottom: 24 }}>
        <Badge kind="fair" style={{ marginBottom: 18 }}>
          B2B ECONOMICS · 7 MIN READ
        </Badge>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(36px, 5vw, 56px)",
            lineHeight: 1.05,
            margin: 0,
            color: "var(--fg-1)",
            letterSpacing: "0.01em",
          }}
        >
          Revenue share or fixed licensing — what's actually better for a mid-tier crypto operator?
        </h1>
        <p
          style={{
            fontSize: 16,
            color: "var(--fg-3)",
            marginTop: 16,
            fontFamily: "var(--font-mono)",
          }}
        >
          2026-05-05
        </p>
      </Section>

      <Section style={{ paddingTop: 0, maxWidth: 760 }}>
        <Prose>
          <p>
            I get this question on every second sales call. The operator has a quote from Spribe (or
            Smartsoft, or Pragmatic Play) for an Aviator-class title at $40-60k setup plus a 5-15% rev
            share, and a quote from a smaller studio at flat $80k a year. Which is actually cheaper
            once you run the math?
          </p>
          <p>
            Short answer: rev-share wins until you cross roughly $150k monthly GGR per game. Below
            that, the fixed-license integration fees and minimum monthly commitments swamp the percent
            you'd otherwise pay. Above it, the percent compounds faster than your traffic is worth.
            Long answer below.
          </p>

          <H2>(a) The two models</H2>
          <p>
            <strong>Revenue share</strong> — operator pays the studio a percentage of GGR (gross
            gaming revenue, i.e. wagers minus payouts) per billing cycle. No setup fee or a token
            setup fee. No monthly minimum. The studio is exposed to your traffic risk; you're exposed
            to a percentage of your upside.
          </p>
          <p>
            <strong>Fixed licensing</strong> — operator pays a one-time integration fee plus an
            annual license. Sometimes a monthly minimum on top. The studio is paid the same whether
            your game does $0 or $1M GGR; you're paid the same whether your traffic doubles or
            collapses.
          </p>
          <p>
            Hybrid models exist (lower percent + lower fixed fee, or rev-share with a monthly cap)
            but the two pure forms are the right starting frame.
          </p>

          <H2>(b) Why rev-share aligns incentives</H2>
          <p>
            Studios with rev-share games have direct visibility into your traffic. If the game
            underperforms, the studio loses revenue too — which means the studio has cause to
            actually iterate. Studios on fixed licensing collect the check whether the game converts
            or not, and most of them quietly stop investing in new features once your check clears.
          </p>
          <p>
            This isn't theoretical. Spribe's Aviator update cadence dropped from monthly during the
            rev-share era to roughly twice a year once the licensing tier became their dominant
            revenue line. BGaming, which kept rev-share as the only commercial model, ships a new
            game every 4-6 weeks.
          </p>
          <p>
            If you're a mid-tier operator, you want a studio that benefits when your players come
            back. That's almost mechanically a rev-share studio.
          </p>

          <H2>(c) Hidden costs of fixed licensing</H2>
          <p>The list price is never the actual price. In 2024-2025 we've seen:</p>
          <ul>
            <li>
              <strong>Integration fees</strong> of $15-50k on top of the license. Most of this is
              "professional services" billed at $300/hr for the studio's engineer to walk yours
              through their adapter.
            </li>
            <li>
              <strong>True-up audits</strong> — annual GGR reconciliation where the studio's auditor
              reviews your wallet logs and bills you for the difference if their rev-share tier
              kicks in. Audit costs ~$10k and the studio almost always finds something.
            </li>
            <li>
              <strong>Monthly minimums</strong> — typical for tier-1 studios on fixed licensing.
              Spribe's enterprise tier has historically carried a $8-12k monthly floor regardless of
              your GGR.
            </li>
            <li>
              <strong>Per-currency surcharges</strong> — adding a new fiat or crypto pair after the
              initial integration commonly costs $2-5k each.
            </li>
            <li>
              <strong>Per-jurisdiction surcharges</strong> — extending the license to a new
              regulatory zone is treated as a renegotiation. Curaçao to MGA can mean a fresh
              integration fee.
            </li>
          </ul>
          <p>
            Quoted numbers we've seen for Spribe Aviator licensing in 2024 were around $50k setup
            plus 5-15% of GGR depending on the tier — and the integration / monthly-minimum line
            items often added another $50-80k in the first year before a single round was played.
          </p>

          <H2>(d) When licensing wins</H2>
          <p>The fixed-license model genuinely wins in three scenarios:</p>
          <ol>
            <li>
              <strong>Very high-volume operators</strong> with predictable GGR above ~$150k/month
              per game. At that level you're paying more in rev-share than you'd ever pay in
              license + minimum. Curve crosses around there for most studios.
            </li>
            <li>
              <strong>White-label operators</strong> who plan to resell access to sub-operators.
              Rev-share contracts almost always forbid sub-licensing; a fixed license can include
              redistribution rights.
            </li>
            <li>
              <strong>Operators committed to a single crash title for the long term.</strong> If
              you've made Aviator your hero game and you're building marketing campaigns around it,
              the marginal cost of switching is high enough that a fixed license amortizes well.
            </li>
          </ol>

          <H2>(e) The 10% calculation — what the two models actually cost</H2>
          <p>
            Worked example. Vault Breaker is 10% of GGR, $0 setup, $0 monthly minimum. A
            representative tier-1 fixed license (Spribe-like enterprise terms, normalized): $50k
            setup amortized over 12 months ($4,167/mo), $5,000/mo minimum, plus 4% rev-share above
            the minimum. Numbers below are the monthly cost to the operator at four GGR brackets:
          </p>
          <Card style={{ padding: 0, overflow: "hidden", marginTop: 12 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1.5fr",
                padding: "12px 18px",
                background: "var(--bg-2)",
                borderBottom: "1px solid var(--line)",
                fontSize: 11,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "var(--fg-3)",
              }}
            >
              <span>Game GGR / mo</span>
              <span>Vault Breaker (10%)</span>
              <span>Tier-1 license</span>
              <span>Delta</span>
            </div>
            {COMPARISON.map((row, i) => (
              <div
                key={row.ggr}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr 1.5fr",
                  padding: "14px 18px",
                  borderTop: i === 0 ? "none" : "1px solid var(--line)",
                  fontFamily: "var(--font-mono)",
                  fontSize: 13,
                  color: "var(--fg-2)",
                }}
              >
                <span style={{ color: "var(--fg-1)" }}>{row.ggr}</span>
                <span>{row.revShare}</span>
                <span>{row.licensing}</span>
                <span style={{ color: "var(--cyan)" }}>{row.delta}</span>
              </div>
            ))}
          </Card>
          <p style={{ fontSize: 12, color: "var(--fg-3)", marginTop: 8 }}>
            Tier-1 license model: $50k setup ÷ 12 months + $5k/mo minimum + 4% of GGR above the
            minimum threshold. Numbers are illustrative; actual quotes vary by studio, region, and
            negotiation.
          </p>

          <H2>The honest take</H2>
          <p>
            If you operate at 5-100k MAU and you're not committed to a single crash title for a
            multi-year horizon, rev-share wins by 30%+ on first-year economics. Beyond that, you
            also keep optionality — switching providers under a rev-share contract is usually 30
            days' notice with no exit fee, while exiting a fixed license mid-term often forfeits
            the prepaid balance.
          </p>
          <p>
            We're rev-share only at Vault Breaker, partly because it's how a new studio earns a
            seat at the table, and partly because it's the model where the studio actually
            benefits from making the game better. If you want to walk through your specific GGR
            curve with us, the contact form below routes straight to my inbox.
          </p>
        </Prose>
      </Section>

      <Section style={{ maxWidth: 760 }}>
        <Card style={{ padding: 22, display: "grid", gap: 10 }}>
          <h3
            style={{
              margin: 0,
              fontFamily: "var(--font-display)",
              fontSize: 18,
              color: "var(--fg-1)",
              textTransform: "uppercase",
              letterSpacing: "0.01em",
            }}
          >
            Run the math on your own numbers
          </h3>
          <p style={{ margin: 0, fontSize: 13, color: "var(--fg-2)", lineHeight: 1.6 }}>
            Send us your projected GGR and we'll send back a one-page comparison against whatever
            quote you're holding from a tier-1 studio. No sales pitch attached.
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Link to="/contact" style={{ textDecoration: "none" }}>
              <Button variant="gold">CONTACT SALES</Button>
            </Link>
            <Link to="/blog" style={{ textDecoration: "none" }}>
              <Button>← FIELD NOTES</Button>
            </Link>
          </div>
        </Card>
      </Section>
    </>
  );
}

function H2({ children }: { children: React.ReactNode }) {
  return (
    <h2
      style={{
        marginTop: 36,
        marginBottom: 8,
        fontFamily: "var(--font-display)",
        fontSize: 22,
        color: "var(--fg-1)",
        letterSpacing: "0.01em",
      }}
    >
      {children}
    </h2>
  );
}

function Prose({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 15,
        color: "var(--fg-2)",
        lineHeight: 1.75,
      }}
    >
      {children}
    </div>
  );
}
